import { api } from "@/api/client"
import {
  ChampionFundHistoryItem,
  ChampionFundHistoryResponse,
  ChampionFundHistoryWinner,
} from "@/types/championFundHistory"

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function pickNumber(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

function pickString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback
}

function normalizeWinner(raw: unknown): ChampionFundHistoryWinner | null {
  const obj = asRecord(raw)
  if (!obj) return null
  return {
    userId: pickNumber(obj.userId ?? obj.user_id),
    nickname: pickString(obj.nickname, "—"),
    publicGameCode: pickString(obj.publicGameCode ?? obj.public_game_code),
    localPts: pickNumber(obj.localPts ?? obj.local_pts),
    prizeCoins: pickNumber(obj.prizeCoins ?? obj.prize_coins ?? obj.coins),
  }
}

function normalizeItem(raw: unknown): ChampionFundHistoryItem | null {
  const obj = asRecord(raw)
  if (!obj) return null
  const winnerRaw = obj.winner
  return {
    eventId: pickString(obj.eventId ?? obj.event_id),
    monthKey: pickString(obj.monthKey ?? obj.month_key),
    seasonTitle: pickString(obj.seasonTitle ?? obj.season_title, "—"),
    occurredAt: pickString(obj.occurredAt ?? obj.occurred_at),
    fundCoins: pickNumber(obj.fundCoins ?? obj.fund_coins),
    winner: winnerRaw != null ? normalizeWinner(winnerRaw) : null,
  }
}

function normalizeHistory(data: unknown): ChampionFundHistoryResponse {
  const root = asRecord(data) ?? {}
  const itemsRaw = Array.isArray(root.items) ? root.items : []
  return {
    cityId: pickNumber(root.cityId ?? root.city_id),
    cityName:
      typeof root.cityName === "string"
        ? root.cityName
        : typeof root.city_name === "string"
          ? root.city_name
          : null,
    items: itemsRaw
      .map(normalizeItem)
      .filter((item): item is ChampionFundHistoryItem => item != null),
    nextCursor:
      typeof root.nextCursor === "string"
        ? root.nextCursor
        : typeof root.next_cursor === "string"
          ? root.next_cursor
          : null,
  }
}

export type ChampionFundHistoryQuery = {
  cityId?: number
  cursor?: string
  limit?: number
}

export async function getChampionFundHistory(
  params: ChampionFundHistoryQuery = {}
): Promise<ChampionFundHistoryResponse> {
  const search = new URLSearchParams()
  if (params.cityId != null) search.set("cityId", String(params.cityId))
  if (params.cursor) search.set("cursor", params.cursor)
  if (params.limit != null) {
    search.set("limit", String(Math.min(50, Math.max(1, params.limit))))
  }
  const qs = search.toString()
  const { data } = await api.get<unknown>(
    `/game/champion-fund/history${qs ? `?${qs}` : ""}`
  )
  return normalizeHistory(data)
}
